import { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
    PASTE_COMMAND,
    COMMAND_PRIORITY_HIGH,
    $getSelection,
    $isRangeSelection,
    $getRoot,
    $createParagraphNode,
    $createTextNode,
} from "lexical";
import { $createTableNodeWithDimensions } from "@lexical/table";
import { $createStyledTextNode } from "../nodes/StyledTextNode";

/**
 * Paste tables from Excel (HTML) or TSV (plain text) into Lexical table nodes,
 * preserving cell formatting (bold, italic, underline, text colour, font size,
 * and cell background colour).
 *
 * Background colour is applied to the TableCellNode directly — NOT to the text
 * node — so it renders as cell shading rather than a text highlight.
 *
 * Runs at COMMAND_PRIORITY_HIGH so it wins over ImagePasteDropPlugin (LOW),
 * which would otherwise capture the bitmap Excel places on the clipboard
 * alongside its HTML table and insert it as a base64 image node.
 */
export default function TablePastePlugin() {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        // ── Style extraction helpers ────────────────────────────────────────────

        /**
         * Normalise Excel colour values to plain hex/rgb strings.
         * Excel sometimes emits "windowtext", "black", or "auto" — drop those
         * so HtmlToOpenXml on the C# side only receives real colour values.
         */
        const normaliseColor = (raw) => {
            if (!raw) return null;
            const lower = raw.trim().toLowerCase();
            if (lower === "windowtext" || lower === "auto" || lower === "black" || lower === "white") return null;
            return raw.trim();
        };

        /**
         * Extract all relevant styles from a <td>/<th> element.
         *
         * - backgroundColor comes from the cell element itself (Excel puts it there)
         * - text styles (color, fontSize, bold, italic, underline) may be on the
         *   cell or on child <span>/<b>/<i> elements — we walk all descendants
         */
        const extractCellStyles = (td) => {
            const styles = {
                // Cell-level
                backgroundColor: normaliseColor(td.style?.backgroundColor),
                // Text-level
                color: null,
                fontSize: null,
                bold: false,
                italic: false,
                underline: false,
            };

            const walk = (node) => {
                if (node.nodeType !== Node.ELEMENT_NODE) return;
                const tag = node.tagName.toLowerCase();
                const s = node.style;

                if (["b", "strong"].includes(tag) || parseInt(s?.fontWeight) >= 700 || s?.fontWeight === "bold") {
                    styles.bold = true;
                }
                if (["i", "em"].includes(tag) || s?.fontStyle === "italic") {
                    styles.italic = true;
                }
                if (["u"].includes(tag) || s?.textDecoration?.includes("underline")) {
                    styles.underline = true;
                }
                if (s?.color && !styles.color) {
                    styles.color = normaliseColor(s.color);
                }
                if (s?.fontSize && !styles.fontSize) {
                    styles.fontSize = s.fontSize;
                }

                for (const child of node.childNodes) walk(child);
            };

            walk(td);
            return styles;
        };

        /**
         * Build a Lexical text node for a cell, applying:
         *   - bold/italic/underline as Lexical format flags
         *   - color/fontSize as StyledTextNode inline styles
         *
         * backgroundColor is handled separately at the cell level.
         */
        const buildTextNode = (text, styles) => {
            const hasInlineStyle = styles.color || styles.fontSize;

            const node = hasInlineStyle
                ? $createStyledTextNode(text, {
                    color: styles.color,
                    fontSize: styles.fontSize,
                })
                : $createTextNode(text);

            // Lexical format bitmask
            let format = 0;
            if (styles.bold) format |= 1;      // IS_BOLD
            if (styles.italic) format |= 2;    // IS_ITALIC
            if (styles.underline) format |= 8; // IS_UNDERLINE
            if (format) node.setFormat(format);

            return node;
        };

        // ── Paste handler ───────────────────────────────────────────────────────

        const handlePaste = (event) => {
            const { clipboardData } = event;
            if (!clipboardData) return false;

            const html = clipboardData.getData("text/html");
            const text = clipboardData.getData("text/plain") || "";

            // 1) HTML with a <table> — Excel / Google Sheets
            if (html && /<table[\s>]/i.test(html)) {
                event.preventDefault();

                editor.update(() => {
                    const dom = new DOMParser().parseFromString(html, "text/html");
                    const allRows = Array.from(dom.querySelectorAll("tr"));
                    if (allRows.length === 0) return;

                    const rowData = allRows.map((tr) =>
                        Array.from(tr.querySelectorAll("td, th")).map((td) => ({
                            text: (td.textContent ?? "").trim(),
                            styles: extractCellStyles(td),
                        }))
                    );

                    const rowCount = rowData.length;
                    const colCount = Math.max(...rowData.map((r) => r.length));

                    const tableNode = $createTableNodeWithDimensions(rowCount, colCount);

                    let rIndex = 0;
                    for (const rowNode of tableNode.getChildren()) {
                        const cells = rowData[rIndex] || [];
                        let cIndex = 0;
                        for (const cellNode of rowNode.getChildren()) {
                            const cell = cells[cIndex];

                            // ✅ Apply background colour to the cell node, not the text
                            if (cell?.styles?.backgroundColor) {
                                cellNode.setBackgroundColor(cell.styles.backgroundColor);
                            }

                            const paragraph = $createParagraphNode();
                            if (cell?.text?.length > 0) {
                                paragraph.append(buildTextNode(cell.text, cell.styles));
                            }
                            cellNode.append(paragraph);
                            cIndex++;
                        }
                        rIndex++;
                    }

                    const selection = $getSelection();
                    if ($isRangeSelection(selection)) {
                        selection.insertNodes([tableNode]);
                    } else {
                        $getRoot().append(tableNode);
                    }
                });

                return true;
            }

            // 2) TSV fallback — no style info available in plain text
            if (text && /\t/.test(text) && /(\r?\n)/.test(text)) {
                event.preventDefault();

                const rows = text
                    .split(/\r?\n/)
                    .filter((r) => r.length > 0)
                    .map((r) => r.split("\t"));

                if (rows.length === 0) return true;

                const rowCount = rows.length;
                const colCount = Math.max(...rows.map((r) => r.length));

                editor.update(() => {
                    const tableNode = $createTableNodeWithDimensions(rowCount, colCount);

                    let rIndex = 0;
                    for (const rowNode of tableNode.getChildren()) {
                        const rowValues = rows[rIndex] || [];
                        let cIndex = 0;
                        for (const cellNode of rowNode.getChildren()) {
                            const value = rowValues[cIndex] ?? "";
                            const paragraph = $createParagraphNode();
                            paragraph.append($createTextNode(value));
                            cellNode.append(paragraph);
                            cIndex++;
                        }
                        rIndex++;
                    }

                    const selection = $getSelection();
                    if ($isRangeSelection(selection)) {
                        selection.insertNodes([tableNode]);
                    } else {
                        $getRoot().append(tableNode);
                    }
                });

                return true;
            }

            return false;
        };

        const unregister = editor.registerCommand(
            PASTE_COMMAND,
            handlePaste,
            COMMAND_PRIORITY_HIGH
        );

        return () => unregister();
    }, [editor]);

    return null;
}